/**
 * platform-pricing-page.tsx — Platform → Bảng giá gói (super-admin).
 * Route `/platform/pricing`.
 *
 * Sửa bảng giá dạng NHÁP rồi bấm "Lưu bảng giá" (PUT /platform/billing/pricing).
 * Khung "Xem thử báo giá" gọi POST /platform/billing/pricing/preview với bản nháp
 * (chưa lưu) để xem ngay số tiền khách phải trả. Server luôn kiểm tra lại — thông
 * báo lỗi của server là chuẩn; kiểm tra phía client chỉ để báo sớm.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import {
  AlertTriangle, Calculator, Loader2, Minus, Plus, RotateCcw, Save, Trash2, Info,
} from 'lucide-react'
import { toast } from 'sonner'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { formatVnd } from '@/lib/format-money'
import { platformApiError } from '@/lib/platform-client'
import { cn } from '@/lib/utils'
import {
  previewPricing, usePlatformPricing, useResetPricing, useSavePricing,
} from '@/hooks/use-platform-pricing'
import { usePlatformAiCatalog, usePlatformAiSettings } from '@/hooks/use-platform-ai'
import { formatTokens } from '@/lib/ai-credits'
import { referenceModelOf, unitsForValue, unitsToTokensRef } from './ai/platform-ai-lib'
import {
  FEATURES_MAX, FEATURE_MAX_LEN, QUOTA_KEYS, QUOTA_LABELS, addTier, featuresToText, formatIntInput, newPlan, parseIntInput, removeTier, textToFeatures,
  DEFAULT_VAT_PERCENT, EINVOICE_VAT_RATES, samePricing, tierRanges, validatePricing,
  type DurationOption, type PriceTier, type PricingConfig, type PricingPlan, type QuotaKey, type Quote, type Quotas,
} from './pricing-editor'

const textareaCls =
  'flex w-full rounded-md border border-input bg-card px-3 py-1.5 text-sm transition-colors placeholder:text-muted-foreground hover:border-primary/60 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15'

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })
}

// ── Trang ─────────────────────────────────────────────────────────
export function PlatformPricingPage() {
  const { data, isLoading, isError } = usePlatformPricing()
  const save = useSavePricing()
  const reset = useResetPricing()

  const [draft, setDraft] = useState<PricingConfig | null>(null)
  // Khoá ổn định cho từng gói (để React không nhầm hàng khi đổi mã) + gói đã có (khoá mã).
  const [planKeys, setPlanKeys] = useState<string[]>([])
  const keySeq = useRef(0)
  const [serverError, setServerError] = useState<string | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)

  function loadDraft(cfg: PricingConfig) {
    setDraft(structuredClone(cfg))
    setPlanKeys(cfg.plans.map((p) => `saved:${p.id}`))
  }

  // Nạp bản nháp lần đầu khi có dữ liệu.
  useEffect(() => {
    if (data && !draft) loadDraft(data.pricing)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const dirty = !!draft && !!data && !samePricing(draft, data.pricing)
  const clientErrors = useMemo(() => (draft ? validatePricing(draft) : []), [draft])

  // Cảnh báo khi rời trang còn thay đổi chưa lưu.
  useEffect(() => {
    if (!dirty) return
    const h = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', h)
    return () => window.removeEventListener('beforeunload', h)
  }, [dirty])
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname)
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    if (window.confirm('Bảng giá có thay đổi chưa lưu. Rời trang và bỏ các thay đổi?')) blocker.proceed()
    else blocker.reset()
  }, [blocker])

  if (isLoading || (data && !draft)) return <Loading label="Đang tải bảng giá..." />
  if (isError || !data || !draft) return <ErrorState message="Không tải được bảng giá." />

  const update = (fn: (d: PricingConfig) => void) =>
    setDraft((prev) => {
      if (!prev) return prev
      const next = structuredClone(prev)
      fn(next)
      return next
    })

  async function onSave() {
    if (!draft) return
    setServerError(null)
    try {
      const res = await save.mutateAsync(draft)
      loadDraft(res.pricing)
      toast.success('Đã lưu bảng giá')
    } catch (err) {
      const msg = platformApiError(err)
      setServerError(msg)
      toast.error(msg)
    }
  }

  async function onReset() {
    setServerError(null)
    try {
      const res = await reset.mutateAsync()
      loadDraft(res.pricing)
      setConfirmReset(false)
      toast.success('Đã khôi phục bảng giá mặc định')
    } catch (err) {
      toast.error(platformApiError(err))
    }
  }

  function addPlan() {
    if (!draft) return
    const p = newPlan(draft.plans.map((x) => x.id))
    update((d) => { d.plans.push(p) })
    setPlanKeys((k) => [...k, `new:${++keySeq.current}`])
  }

  function removePlan(i: number) {
    update((d) => { d.plans.splice(i, 1) })
    setPlanKeys((k) => k.filter((_, j) => j !== i))
  }

  const status = data.isDefault
    ? 'Đang dùng bảng giá mặc định'
    : data.updatedAt ? `Cập nhật lúc ${fmtDateTime(data.updatedAt)}` : 'Đã lưu'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-medium">Bảng giá gói</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Giá gói, phụ phí mua thêm nhân viên / kênh và giảm giá theo thời hạn — áp dụng cho trang Gói dịch vụ của mọi tổ chức.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={data.isDefault ? 'secondary' : 'default'}>{status}</Badge>
            {dirty && <Badge variant="warning">Có thay đổi chưa lưu</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <Button variant="ghost" onClick={() => { loadDraft(data.pricing); setServerError(null) }}>
              Bỏ thay đổi
            </Button>
          )}
          <Button variant="outline" onClick={() => setConfirmReset(true)} disabled={data.isDefault && !dirty}>
            <RotateCcw className="h-4 w-4" /> Khôi phục mặc định
          </Button>
          <Button onClick={onSave} disabled={!dirty || save.isPending}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Lưu bảng giá
          </Button>
        </div>
      </div>

      {serverError && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div><span className="font-medium">Không lưu được:</span> {serverError}</div>
        </div>
      )}
      {clientErrors.length > 0 && (
        <div className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 text-sm">
          <p className="flex items-center gap-2 font-medium text-warning">
            <AlertTriangle className="h-4 w-4" /> Cần sửa trước khi lưu ({clientErrors.length})
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-6 text-muted-foreground">
            {clientErrors.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}
            {clientErrors.length > 8 && <li>… và {clientErrors.length - 8} lỗi khác</li>}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-6">
          {/* Gói dịch vụ */}
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div>
                <CardTitle>Gói dịch vụ</CardTitle>
                <CardDescription>Giá gốc / tháng đã gồm sẵn một số nhân viên và kênh. Để trống giá = "Liên hệ".</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={addPlan} disabled={draft.plans.length >= 8}>
                <Plus className="h-4 w-4" /> Thêm gói
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {draft.plans.map((p, i) => (
                <PlanEditor
                  key={planKeys[i] ?? i}
                  plan={p}
                  idLocked={(planKeys[i] ?? '').startsWith('saved:')}
                  onChange={(fn) => update((d) => fn(d.plans[i]))}
                  onRemove={p.id === 'free' && (planKeys[i] ?? '').startsWith('saved:') ? undefined : () => removePlan(i)}
                />
              ))}
            </CardContent>
          </Card>

          {/* Phụ phí luỹ tiến */}
          <Card>
            <CardHeader>
              <CardTitle>Phụ phí mua thêm (luỹ tiến)</CardTitle>
              <CardDescription>
                Mỗi đơn vị vượt mức gồm sẵn tính theo đơn giá của bậc chứa số thứ tự của nó (đơn vị thứ k), VND / đơn vị / tháng.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
              {QUOTA_KEYS.map((k) => (
                <TierEditor
                  key={k}
                  label={QUOTA_LABELS[k]}
                  tiers={draft.addons[k]}
                  onChange={(tiers) => update((d) => { d.addons[k] = tiers })}
                />
              ))}
            </CardContent>
          </Card>

          {/* Thời hạn */}
          <Card>
            <CardHeader>
              <CardTitle>Thời hạn &amp; giảm giá</CardTitle>
              <CardDescription>Khách mua theo số ngày; mỗi mốc có % giảm riêng. Chọn một mốc làm mặc định.</CardDescription>
            </CardHeader>
            <CardContent>
              <DurationsEditor
                durations={draft.durations}
                defaultDays={draft.defaultDays}
                onChange={(durations, defaultDays) => update((d) => { d.durations = durations; d.defaultDays = defaultDays })}
              />
            </CardContent>
          </Card>

          {/* Khác */}
          <Card>
            <CardHeader>
              <CardTitle>Khác</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
              <div className="space-y-1.5">
                <Label>Bước làm tròn thành tiền (₫)</Label>
                <IntInput value={draft.roundTo} onChange={(v) => update((d) => { d.roundTo = v ?? 1 })} />
                <p className="text-xs text-muted-foreground">Vd 1.000 → làm tròn tới nghìn đồng.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Ghi chú dưới bảng giá</Label>
                <textarea
                  className={textareaCls}
                  rows={2}
                  maxLength={300}
                  value={draft.note}
                  onChange={(e) => update((d) => { d.note = e.target.value })}
                  placeholder="Giá niêm yết chưa gồm VAT — VAT cộng khi thanh toán."
                />
              </div>
              <VatField
                value={draft.vatPercent ?? DEFAULT_VAT_PERCENT}
                onChange={(v) => update((d) => { d.vatPercent = v })}
              />
              <div className="space-y-1.5 md:col-span-2">
                <Label>Tỷ giá thu PayPal (₫ / 1 USD)</Label>
                <IntInput
                  className="w-[200px]"
                  value={draft.paypalVndPerUsd ?? null}
                  suffix="₫"
                  placeholder="Theo cấu hình server"
                  onChange={(v) => update((d) => { if (v == null) delete d.paypalVndPerUsd; else d.paypalVndPerUsd = v })}
                />
                <p className="text-xs text-muted-foreground">
                  PayPal chỉ nhận USD. Số USD của đơn = số tiền VND ÷ tỷ giá, làm tròn lên đến cent và chốt lúc khách đặt hàng; đơn, VAT và hoá đơn vẫn bằng VND.
                  Để trống = dùng cấu hình máy chủ (biến <code>EVOPAY_PAYPAL_VND_PER_USD</code>); không có cả hai thì PayPal bị ẩn. Từ 1.000 đến 1.000.000.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Xem thử */}
        <div className="xl:sticky xl:top-0 xl:self-start">
          <PreviewPanel draft={draft} />
        </div>
      </div>

      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Khôi phục bảng giá mặc định?</DialogTitle>
            <DialogDescription>
              Bảng giá đã lưu sẽ bị xoá và hệ thống quay về bảng giá mặc định. Các tổ chức đã mua vẫn giữ nguyên thời hạn đã trả.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReset(false)}>Huỷ</Button>
            <Button variant="destructive" onClick={onReset} disabled={reset.isPending}>
              {reset.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Khôi phục
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Ô nhập số nguyên (có dấu chấm ngăn nghìn) ─────────────────────
function IntInput({
  value, onChange, placeholder, className, disabled, suffix,
}: {
  value: number | null
  onChange: (v: number | null) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  suffix?: string
}) {
  return (
    <div className={cn('relative', className)}>
      <Input
        inputMode="numeric"
        value={formatIntInput(value)}
        onChange={(e) => onChange(parseIntInput(e.target.value))}
        placeholder={placeholder}
        disabled={disabled}
        className={cn('text-right tabular-nums', suffix && 'pr-7')}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{suffix}</span>
      )}
    </div>
  )
}

// ── Một gói ───────────────────────────────────────────────────────
function PlanEditor({
  plan, idLocked, onChange, onRemove,
}: {
  plan: PricingPlan
  idLocked: boolean
  onChange: (fn: (p: PricingPlan) => void) => void
  onRemove?: () => void
}) {
  // Giữ chữ đang gõ (kể cả dòng trống) — chỉ đồng bộ lại khi danh sách bị đổi từ bên ngoài (đặt lại / nạp bản mới).
  const [featuresDraft, setFeaturesDraft] = useState(() => featuresToText(plan.features))
  // Quyền lợi AI: lưu theo đơn vị nội bộ của ví, nhập / hiện theo đ (giá bán ra) + ≈ token theo model tham chiếu.
  const aiSettings = usePlatformAiSettings()
  const aiCatalog = usePlatformAiCatalog()
  const unitVnd = aiSettings.data?.creditValueVnd ?? 0
  const refModel = referenceModelOf(aiCatalog.data)
  const toVnd = (units: number | null | undefined) => (units == null ? null : unitVnd ? Math.round(units * unitVnd) : units)
  const toUnits = (v: number | null) => (v == null ? null : unitVnd ? unitsForValue(v, { creditValueVnd: unitVnd }) : v)
  const approx = (units: number | null | undefined) => {
    if (!units || !aiSettings.data) return null
    const t = unitsToTokensRef(units, aiSettings.data, refModel)
    return t == null ? null : `≈ ${formatTokens(t)}${refModel ? ` ${refModel.title}` : ''}`
  }
  useEffect(() => {
    setFeaturesDraft((d) => (textToFeatures(d).join('\n') === (plan.features ?? []).join('\n') ? d : featuresToText(plan.features)))
  }, [plan.features])
  return (
    <div className={cn('rounded-lg border p-4', plan.highlight && 'border-primary/50 bg-primary/[0.02]', plan.hidden && 'opacity-80')}>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_160px_180px_auto]">
        <div className="space-y-1.5">
          <Label>Tên gói</Label>
          <Input value={plan.name} maxLength={40} onChange={(e) => onChange((p) => { p.name = e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Mã</Label>
          <Input
            value={plan.id}
            disabled={idLocked}
            maxLength={32}
            className="font-mono text-xs"
            title={idLocked ? 'Mã gói đã dùng — không đổi được' : 'Chữ thường, số, "-", "_"'}
            onChange={(e) => onChange((p) => { p.id = e.target.value.toLowerCase().replace(/\s+/g, '-') })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Giá / tháng</Label>
          <IntInput value={plan.monthlyPrice} placeholder="Liên hệ" suffix="₫" onChange={(v) => onChange((p) => { p.monthlyPrice = v })} />
        </div>
        <div className="flex items-end justify-end">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Xoá gói"
            title={onRemove ? 'Xoá gói' : 'Không xoá được gói "free" (gói khi hết hạn sẽ hạ về)'}
            disabled={!onRemove}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        <Label>Mô tả</Label>
        <Input value={plan.description} maxLength={200} onChange={(e) => onChange((p) => { p.description = e.target.value })} />
      </div>

      <div className="mt-3 space-y-1.5">
        <Label>Tính năng hiển thị</Label>
        <Input
          value={plan.featuresIntro ?? ''}
          maxLength={80}
          placeholder='Dòng mở đầu (tuỳ chọn), vd "Tất cả trong gói Cơ bản, thêm:"'
          onChange={(e) => onChange((p) => { p.featuresIntro = e.target.value })}
        />
        <textarea
          rows={4}
          className={cn(textareaCls, 'min-h-[88px]')}
          value={featuresDraft}
          placeholder="Mỗi dòng một tính năng"
          onChange={(e) => { setFeaturesDraft(e.target.value); onChange((p) => { p.features = textToFeatures(e.target.value) }) }}
        />
        <p className="text-[11px] text-muted-foreground">
          Hiện ở trang Gói dịch vụ và popup chào mừng sau khi khách đăng ký (gói Miễn phí). Tối đa {FEATURES_MAX} dòng, mỗi dòng {FEATURE_MAX_LEN} ký tự.
        </p>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th className="py-1 text-left font-normal">Hạn mức</th>
              <th className="py-1 text-right font-normal">Gồm sẵn</th>
              <th className="py-1 text-right font-normal">Tối đa (trống = không giới hạn)</th>
            </tr>
          </thead>
          <tbody>
            {QUOTA_KEYS.map((k) => (
              <tr key={k}>
                <td className="py-1 pr-3">{QUOTA_LABELS[k]}</td>
                <td className="w-40 py-1 pl-2">
                  <IntInput value={plan.included[k]} onChange={(v) => onChange((p) => { p.included[k] = v ?? 0 })} />
                </td>
                <td className="w-56 py-1 pl-2">
                  <IntInput value={plan.max[k]} placeholder="Không giới hạn" onChange={(v) => onChange((p) => { p.max[k] = v })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Khách hoạt động / tháng</Label>
          <IntInput value={plan.activeContactsPerMonth} placeholder="Không giới hạn" onChange={(v) => onChange((p) => { p.activeContactsPerMonth = v })} />
        </div>
        <div className="space-y-1.5">
          <Label>AI trả lời / ngày</Label>
          <IntInput value={plan.aiRepliesPerDay} placeholder="Không giới hạn" onChange={(v) => onChange((p) => { p.aiRepliesPerDay = v })} />
        </div>
      </div>

      {/* Quyền lợi AI hai chế độ (token) — áp dụng từ kỳ gói tiếp theo của mỗi tổ chức. */}
      <div className="mt-3 rounded-lg border border-dashed p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          Quyền lợi AI của eCDP — nhập theo giá trị đ (giá bán ra); khách thấy ≈ token theo model tổ chức của họ.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Token AI tặng / tháng</Label>
            <IntInput
              value={toVnd(plan.aiCreditsPerMonth)} placeholder="Không tặng" suffix={unitVnd ? 'đ' : undefined}
              onChange={(v) => onChange((p) => { p.aiCreditsPerMonth = toUnits(v) })}
            />
            {approx(plan.aiCreditsPerMonth) && <p className="text-xs text-muted-foreground">{approx(plan.aiCreditsPerMonth)}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Nhúng miễn phí / ngày</Label>
            <IntInput value={plan.aiEmbedFreePerDay ?? null} placeholder="Theo mặc định" onChange={(v) => onChange((p) => { p.aiEmbedFreePerDay = v })} />
          </div>
          <div className="space-y-1.5">
            <Label>Trần token AI eCDP / ngày</Label>
            <IntInput
              value={toVnd(plan.aiPlatformDailyCap)} placeholder="Không trần" suffix={unitVnd ? 'đ' : undefined}
              onChange={(v) => onChange((p) => { p.aiPlatformDailyCap = toUnits(v) })}
            />
            {approx(plan.aiPlatformDailyCap) && <p className="text-xs text-muted-foreground">{approx(plan.aiPlatformDailyCap)}</p>}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <SwitchField label="Bán trực tuyến" checked={plan.purchasable} onChange={(v) => onChange((p) => { p.purchasable = v })} />
        <SwitchField label="Nổi bật" checked={plan.highlight} onChange={(v) => onChange((p) => { p.highlight = v })} />
        <SwitchField label="Ẩn khỏi trang khách" checked={plan.hidden} onChange={(v) => onChange((p) => { p.hidden = v })} />
      </div>
    </div>
  )
}

/** Thuế suất VAT cộng khi thanh toán (Q2): 0–100, "Không chịu thuế" = 0. */
function VatField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const noTax = value === 0
  const einvoiceOk = (EINVOICE_VAT_RATES as readonly number[]).includes(value)
  return (
    <div className="space-y-1.5 md:col-span-2">
      <Label>Thuế suất VAT cộng khi thanh toán</Label>
      <div className="flex flex-wrap items-center gap-4">
        <IntInput
          className="w-[120px]"
          value={value}
          suffix="%"
          disabled={noTax}
          onChange={(v) => onChange(Math.min(100, v ?? 0))}
        />
        <SwitchField label="Không chịu thuế (0%)" checked={noTax} onChange={(v) => onChange(v ? 0 : DEFAULT_VAT_PERCENT)} />
      </div>
      <p className="text-xs text-muted-foreground">
        Giá niêm yết là giá CHƯA gồm VAT. Mọi đơn thanh toán cộng thêm VAT theo thuế suất này (từ 0 đến 100%).
        Hoa hồng đối tác và quy đổi gói cũ tính trên số tiền trước VAT.
      </p>
      {!einvoiceOk && (
        <p className="text-xs text-amber-600">Hoá đơn điện tử chỉ hỗ trợ 0%, 5%, 8%, 10% — đơn với thuế suất {value}% sẽ không xuất được hoá đơn.</p>
      )}
    </div>
  )
}

function SwitchField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-2">
      <Switch checked={checked} onCheckedChange={onChange} />
      <span>{label}</span>
    </label>
  )
}

// ── Bậc giá ───────────────────────────────────────────────────────
function TierEditor({ label, tiers, onChange }: { label: string; tiers: PriceTier[]; onChange: (t: PriceTier[]) => void }) {
  const ranges = tierRanges(tiers)
  const set = (i: number, patch: Partial<PriceTier>) => onChange(tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)))
  return (
    <div className="rounded-lg border p-3">
      <p className="mb-2 text-sm font-medium">{label}</p>
      <div className="grid grid-cols-[40px_minmax(72px,110px)_minmax(120px,1fr)_28px] items-center gap-1.5 text-xs text-muted-foreground">
        <span>Từ</span><span>Đến</span><span className="text-right">Đơn giá (₫ / đơn vị / tháng)</span><span />
      </div>
      <div className="mt-1 space-y-1.5">
        {tiers.map((t, i) => {
          const last = i === tiers.length - 1
          return (
            <div key={i} className="grid grid-cols-[40px_minmax(72px,110px)_minmax(120px,1fr)_28px] items-center gap-1.5">
              <span className="text-sm tabular-nums text-muted-foreground">{formatIntInput(ranges[i].from)}</span>
              {last ? (
                <span className="px-1 text-xs text-muted-foreground">trở lên</span>
              ) : (
                <IntInput value={t.upTo} onChange={(v) => set(i, { upTo: v })} />
              )}
              <IntInput value={t.unitPrice} placeholder="0" suffix="₫" onChange={(v) => set(i, { unitPrice: v ?? 0 })} />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="Xoá bậc"
                disabled={tiers.length <= 1}
                onClick={() => onChange(removeTier(tiers, i))}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )
        })}
      </div>
      <Button variant="link" size="sm" className="mt-1 h-7 px-0" disabled={tiers.length >= 10} onClick={() => onChange(addTier(tiers))}>
        <Plus className="h-3.5 w-3.5" /> Thêm bậc
      </Button>
    </div>
  )
}

// ── Thời hạn ──────────────────────────────────────────────────────
function DurationsEditor({
  durations, defaultDays, onChange,
}: {
  durations: DurationOption[]
  defaultDays: number
  onChange: (d: DurationOption[], defaultDays: number) => void
}) {
  function set(i: number, patch: Partial<DurationOption>) {
    const next = durations.map((d, j) => (j === i ? { ...d, ...patch } : d))
    // Đổi số ngày của mốc mặc định thì mặc định đi theo.
    const nextDefault = patch.days != null && durations[i].days === defaultDays ? patch.days : defaultDays
    onChange(next, nextDefault)
  }
  function remove(i: number) {
    const next = durations.filter((_, j) => j !== i)
    const nextDefault = durations[i].days === defaultDays ? next[0]?.days ?? defaultDays : defaultDays
    onChange(next, nextDefault)
  }
  function add() {
    const maxDays = durations.reduce((m, d) => Math.max(m, d.days), 0)
    onChange([...durations, { days: Math.min(1100, maxDays + 30), discountPercent: 0, badge: '' }], defaultDays)
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-sm">
        <thead>
          <tr className="text-xs text-muted-foreground">
            <th className="py-1 text-left font-normal">Số ngày</th>
            <th className="py-1 text-left font-normal">% giảm</th>
            <th className="py-1 text-left font-normal">Nhãn góc</th>
            <th className="py-1 text-center font-normal">Mặc định</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {durations.map((d, i) => (
            <tr key={i}>
              <td className="w-32 py-1 pr-2"><IntInput value={d.days} suffix="ngày" className="[&_input]:pr-10" onChange={(v) => set(i, { days: v ?? 0 })} /></td>
              <td className="w-28 py-1 pr-2"><IntInput value={d.discountPercent} suffix="%" onChange={(v) => set(i, { discountPercent: v ?? 0 })} /></td>
              <td className="py-1 pr-2">
                <Input
                  value={d.badge}
                  maxLength={16}
                  placeholder={d.discountPercent > 0 ? `−${d.discountPercent}%` : 'vd Dùng thử'}
                  onChange={(e) => set(i, { badge: e.target.value })}
                />
              </td>
              <td className="w-24 py-1 text-center">
                <input
                  type="radio"
                  name="defaultDays"
                  className="h-4 w-4 accent-[hsl(var(--primary))]"
                  checked={d.days === defaultDays}
                  onChange={() => onChange(durations, d.days)}
                  aria-label={`Mặc định ${d.days} ngày`}
                />
              </td>
              <td className="w-10 py-1 text-right">
                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Xoá mốc" disabled={durations.length <= 1} onClick={() => remove(i)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button variant="link" size="sm" className="mt-1 h-7 px-0" disabled={durations.length >= 8} onClick={add}>
        <Plus className="h-3.5 w-3.5" /> Thêm mốc thời hạn
      </Button>
    </div>
  )
}

// ── Xem thử báo giá ───────────────────────────────────────────────
function PreviewPanel({ draft }: { draft: PricingConfig }) {
  const plans = draft.plans.filter((p) => p.purchasable && !p.hidden && p.monthlyPrice != null)
  const [planId, setPlanId] = useState<string>(() => (plans.find((p) => p.highlight) ?? plans[0])?.id ?? '')
  const [days, setDays] = useState<number>(draft.defaultDays)
  const [qty, setQty] = useState<Quotas | null>(null)
  const [quote, setQuote] = useState<Quote | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const plan = plans.find((p) => p.id === planId) ?? plans[0]
  const effectiveDays = draft.durations.some((d) => d.days === days) ? days : draft.defaultDays
  const quantities: Quotas = qty ?? (plan ? { ...plan.included } : { members: 0, officialChannels: 0, personalChannels: 0 })

  // Đổi gói → số lượng về mức gồm sẵn.
  useEffect(() => { setQty(null) }, [plan?.id])

  const body = JSON.stringify({ pricing: draft, planId: plan?.id, days: effectiveDays, quantities })
  useEffect(() => {
    if (!plan) { setQuote(null); setError(null); return }
    const ctl = new AbortController()
    setLoading(true)
    const t = window.setTimeout(async () => {
      try {
        const q = await previewPricing(JSON.parse(body), ctl.signal)
        setQuote(q)
        setError(null)
      } catch (err) {
        if (ctl.signal.aborted) return
        setQuote(null)
        setError(platformApiError(err))
      } finally {
        if (!ctl.signal.aborted) setLoading(false)
      }
    }, 300)
    return () => { ctl.abort(); window.clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body])

  const setQ = (k: QuotaKey, v: number) => setQty({ ...quantities, [k]: v })

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Calculator className="h-4 w-4 text-primary" /> Xem thử báo giá</CardTitle>
          <CardDescription>Tính bằng bảng giá đang sửa (chưa cần lưu).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!plan ? (
            <p className="text-sm text-muted-foreground">Chưa có gói nào bật "Bán trực tuyến" và đang hiện.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>Gói</Label>
                  <Select value={plan.id} onValueChange={setPlanId}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name || p.id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Thời hạn</Label>
                  <Select value={String(effectiveDays)} onValueChange={(v) => setDays(Number(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {draft.durations.map((d, i) => (
                        <SelectItem key={`${d.days}-${i}`} value={String(d.days)}>
                          {d.days} ngày{d.discountPercent > 0 ? ` (−${d.discountPercent}%)` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {QUOTA_KEYS.map((k) => (
                <Stepper
                  key={k}
                  label={QUOTA_LABELS[k]}
                  hint={`gồm sẵn ${plan.included[k]}${plan.max[k] != null ? ` · tối đa ${plan.max[k]}` : ''}`}
                  value={quantities[k]}
                  min={plan.included[k]}
                  max={plan.max[k]}
                  onChange={(v) => setQ(k, v)}
                />
              ))}

              <div className="border-t pt-3">
                {error ? (
                  <div className="flex items-start gap-2 rounded-md bg-destructive/5 px-3 py-2 text-sm text-destructive">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
                  </div>
                ) : quote ? (
                  <QuoteBreakdown quote={quote} loading={loading} />
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Đang tính…</div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <FormulaBox />
    </div>
  )
}

function Stepper({
  label, hint, value, min, max, onChange,
}: {
  label: string
  hint: string
  value: number
  min: number
  max: number | null
  onChange: (v: number) => void
}) {
  const clamp = (v: number) => Math.max(min, max == null ? v : Math.min(max, v))
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="icon" className="h-7 w-7" aria-label={`Giảm ${label}`} disabled={value <= min} onClick={() => onChange(clamp(value - 1))}>
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <Input
          inputMode="numeric"
          className="h-7 w-14 px-1 text-center tabular-nums"
          value={String(value)}
          onChange={(e) => {
            // Cho gõ tự do (vd "12" đi qua "1"); dưới mức gồm sẵn thì server báo lỗi, vượt tối đa thì chặn.
            const v = parseIntInput(e.target.value) ?? 0
            onChange(max == null ? v : Math.min(max, v))
          }}
          onBlur={() => onChange(clamp(value))}
        />
        <Button variant="outline" size="icon" className="h-7 w-7" aria-label={`Tăng ${label}`} disabled={max != null && value >= max} onClick={() => onChange(clamp(value + 1))}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}

function QuoteBreakdown({ quote, loading }: { quote: Quote; loading: boolean }) {
  return (
    <div className={cn('space-y-1.5 text-sm transition-opacity', loading && 'opacity-60')}>
      <Row label={`Giá gốc gói ${quote.planName}`} value={formatVnd(quote.basePerMonth)} suffix="/tháng" />
      {quote.lines.map((l) => (
        <Row
          key={l.key}
          label={`Phụ phí ${l.label.toLowerCase()} (+${l.extra})`}
          value={formatVnd(l.monthly)}
          suffix="/tháng"
          muted={l.extra === 0}
        />
      ))}
      <Row label="Giá / tháng" value={formatVnd(quote.perMonth)} strong />
      <Row label={`Tạm tính (× ${quote.days} ngày / 30)`} value={formatVnd(quote.subtotal)} />
      <Row label={`Giảm giá (${quote.discountPercent}%)`} value={quote.discount > 0 ? `−${formatVnd(quote.discount)}` : formatVnd(0)} muted={quote.discount === 0} />
      {quote.vat && (
        <>
          <Row label="Tạm tính (chưa VAT)" value={formatVnd(quote.vat.preTax)} strong />
          <Row label={quote.vat.percent > 0 ? `VAT ${quote.vat.percent}%` : 'VAT (không chịu thuế)'} value={formatVnd(quote.vat.amount)} muted={quote.vat.amount === 0} />
        </>
      )}
      <div className="mt-2 flex items-baseline justify-between border-t pt-2">
        <span className="font-medium">{quote.vat ? 'Tổng thanh toán' : 'Thành tiền'}</span>
        <span className="text-lg font-semibold text-primary tabular-nums">{formatVnd(quote.total)}</span>
      </div>
    </div>
  )
}

function Row({ label, value, suffix, muted, strong }: { label: string; value: string; suffix?: string; muted?: boolean; strong?: boolean }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-3', muted && 'text-muted-foreground', strong && 'font-medium')}>
      <span className="min-w-0">{label}</span>
      <span className="shrink-0 tabular-nums">
        {value}{suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
      </span>
    </div>
  )
}

function FormulaBox() {
  return (
    <div className="rounded-lg border border-primary/20 bg-primary/[0.03] p-4 text-sm">
      <p className="mb-2 flex items-center gap-2 font-medium"><Info className="h-4 w-4 text-primary" /> Cách tính tiền</p>
      <ul className="space-y-1.5 text-muted-foreground">
        <li><span className="text-foreground">Phụ phí</span> (từng loại) = Σ đơn giá bậc cho mỗi đơn vị vượt mức gồm sẵn</li>
        <li><span className="text-foreground">Giá / tháng</span> = giá gốc + Σ phụ phí</li>
        <li><span className="text-foreground">Tạm tính</span> = giá / tháng × số ngày / 30</li>
        <li><span className="text-foreground">Thành tiền (chưa VAT)</span> = làm tròn(tạm tính × (1 − % giảm) − mã ưu đãi, bước làm tròn) − quy đổi gói cũ</li>
        <li><span className="text-foreground">VAT</span> = thành tiền × thuế suất (làm tròn tới 1 ₫); <span className="text-foreground">Tổng thanh toán</span> = thành tiền + VAT</li>
      </ul>
      <p className="mt-2 text-xs text-muted-foreground">
        Vd bậc nhân viên 1–5: 150.000 ₫, 6–10: 120.000 ₫ — gói gồm sẵn 5, mua 7 thì 2 người thêm là người thứ 6, 7 → 2 × 120.000 ₫.
      </p>
    </div>
  )
}
